import { Box, ChakraProps, Grid, GridItem, HStack, Link, Text, VStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { SIGN_IN_MIN_HEIGHT, SIGN_IN_MIN_WIDTH } from "constants/layout";
import { IChildren } from "lib/common/interfaces";
import React from "react";
import { NavLogoWhite } from "styles/icons";
import { BG_GRADIENT, GLASS_BG, GLASS_BORDER, GLOW_BLUE, LOGO_GLOW, ORB_BLUE, ORB_PURPLE } from "styles/theme";
import { ISignInLayoutProps, ISignInLinkProps } from "./interfaces";

function CardContainer({ children }: IChildren) {
  return (
    <Box
      data-testid="content-card"
      width="420px"
      padding="42px"
      bg={GLASS_BG}
      border="1px solid"
      borderColor={GLASS_BORDER}
      borderRadius="3xl"
      boxShadow={`${GLOW_BLUE}, 0 8px 32px rgba(0,0,0,0.3)`}
      backdropFilter="blur(24px)"
      textAlign="left"
    >
      {children}
    </Box>
  );
}

function LogoContainer({ children }: IChildren) {
  return (
    <Box marginBottom="30px">
      {children}
    </Box>
  );
}

function Title({ children, ...rest }: IChildren & ChakraProps) {
  return (
    <Box
      as="h1"
      marginBottom="10px"
      fontSize="4xl"
      fontWeight="bold"
      color="white"
      {...rest}
    >
      {children}
    </Box>
  );
}

export function SubTitleContent({ children }: IChildren) {
  return (
    <Text textStyle="cardBody" color="whiteAlpha.800">{children}</Text>
  );
}

function SubTitle({ children, ...rest }: IChildren & ChakraProps) {
  return (
    <Box marginBottom="10px" {...rest}>
      {children}
    </Box>
  );
}

export function SignInContent({ children, title, subTitle, titleTestId, subTitleTestId }: IChildren & ISignInLayoutProps) {
  return (
    <>
      <Title data-testid={titleTestId}>{title}</Title>
      <SubTitle data-testid={subTitleTestId}>{subTitle}</SubTitle>
      {children}
    </>
  );
}

export function SignInLink({ children, href, ...rest }: IChildren & ISignInLinkProps) {
  return (
    <Link
      href={href}
      marginTop="13px"
      color="#5B6ABF"
      textDecor="underline"
      fontWeight="600"
      {...rest}
    >
      {children}
    </Link>
  );
}

function LoadingContainer({ children }: IChildren) {
  return (
    <Box
      textAlign="center"
      marginTop="40px"
      data-testid="loading-container"
    >
      {children}
    </Box>
  );
}

const bounce = keyframes`
  0%, 100% { transform: scaleY(0.35); opacity: 0.55; }
  50%      { transform: scaleY(1);    opacity: 1; }
`;

// Heights echo the IXICO logo's stepped bars; delays stagger the wave.
const LOADING_BARS = [
  { h: 26, delay: 0 },
  { h: 44, delay: 0.12 },
  { h: 60, delay: 0.24 },
  { h: 44, delay: 0.36 },
  { h: 26, delay: 0.48 }
];

export const LoadingProgress = React.memo(() => {
  return (
    <LoadingContainer>
      <HStack spacing="7px" h="60px" align="center" justify="center" data-testid="loader">
        {LOADING_BARS.map((bar, i) => (
          <Box
            key={i}
            w="9px"
            h={`${bar.h}px`}
            borderRadius="full"
            background="linear-gradient(to bottom, #3b82f6, #8b5cf6)"
            transformOrigin="center"
            boxShadow={GLOW_BLUE}
            sx={{ animation: `${bounce} 1.1s ease-in-out ${bar.delay}s infinite` }}
          />
        ))}
      </HStack>
    </LoadingContainer>
  );
}, () => true);

export function SignInLayout({ children }: IChildren) {
  return (
    <Grid
      data-testid="sign-in-layout"
      width="100vw"
      height="100vh"
      minWidth={SIGN_IN_MIN_WIDTH}
      minHeight={SIGN_IN_MIN_HEIGHT}
      justifyContent="center"
      alignContent="center"
      overflowY="auto"
      position="relative"
      bg={BG_GRADIENT}
    >
      <Box position="absolute" top="80px" left="80px" w="384px" h="384px" borderRadius="full"
        bg={ORB_BLUE} filter="blur(80px)" pointerEvents="none" />
      <Box position="absolute" bottom="80px" right="80px" w="320px" h="320px" borderRadius="full"
        bg={ORB_PURPLE} filter="blur(80px)" pointerEvents="none" />

      <GridItem position="relative">
        <VStack spacing={0}>
          <LogoContainer>
            <NavLogoWhite w="auto" height="48px" filter={LOGO_GLOW} />
          </LogoContainer>
          <CardContainer>
            {children}
          </CardContainer>
        </VStack>
      </GridItem>
    </Grid>
  );
}
